import { floatingIpMonthly, volumeMonthly, type Me } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import {
  CheckCircle2,
  Circle,
  Globe,
  KeyRound,
  Network,
  Plus,
  Server,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFirewalls } from "../api/firewalls.js";
import { useNetworks } from "../api/networks.js";
import { useProjectActivities, useRunningOperations } from "../api/operations.js";
import { usePublicIps } from "../api/public-ips.js";
import { useCatalogue, useProject, useSshKeys } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { useVolumes } from "../api/volumes.js";
import { useFloatingIps } from "../api/floating-ips.js";
import { ErrorMessage } from "../ui/feedback.js";
import { money, relativeTime } from "../ui/format.js";
import { FieldHint, HelpLink } from "../ui/help.js";
import { PageHeader, Section } from "../ui/layout.js";
import { Card, Spinner } from "../ui/primitives.js";
import { AskAssistantButton, operationQuestion } from "../assistant/ask.js";
import { MonthSummary } from "./costs/summary.js";
import { estimateMonthly } from "./overview-cost.js";
import { ConnectionBadges } from "./projects.js";

function Step({
  done,
  title,
  children,
  action,
}: {
  done: boolean;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex gap-3 py-3">
      {done ? (
        <CheckCircle2
          className="mt-0.5 size-5 shrink-0 text-ok"
          role="img"
          aria-label={t("overview.done")}
        />
      ) : (
        <Circle
          className="mt-0.5 size-5 shrink-0 text-muted"
          role="img"
          aria-label={t("overview.todo")}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className={clsx("text-sm font-medium", done && "text-muted line-through")}>{title}</p>
        <p className="text-sm text-muted">{children}</p>
        {action && !done && <div className="mt-1">{action}</div>}
      </div>
    </li>
  );
}

/** Project pages the overview links to. */
type ProjectPath =
  | "/projects/$projectId/servers"
  | "/projects/$projectId/servers/new"
  | "/projects/$projectId/networks"
  | "/projects/$projectId/firewalls"
  | "/projects/$projectId/ssh-keys"
  | "/projects/$projectId/ips"
  | "/projects/$projectId/catalogue";

/** One resource type: how many there are, a short detail, and the way to its list. */
function Counter({
  icon: Icon,
  label,
  count,
  detail,
  to,
  projectId,
}: {
  icon: LucideIcon;
  label: string;
  count: number | undefined;
  detail?: string | undefined;
  to: ProjectPath;
  projectId: string;
}) {
  return (
    <Link
      to={to}
      params={{ projectId }}
      className="flex items-center gap-4 rounded-xl border border-line bg-surface p-4 shadow-sm hover:border-accent"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
        <Icon className="size-5" aria-hidden />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-muted">{label}</span>
        <span className="text-xl font-semibold">{count ?? "—"}</span>
        {detail && <span className="truncate text-xs text-muted">{detail}</span>}
      </div>
    </Link>
  );
}

/** Project start page: what runs in it, what it costs, what is happening, what to do next. */
export function OverviewPage({ projectId, me }: { projectId: string; me: Me }) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const types = useCatalogue(projectId, "server-types").data;
  const prices = useCatalogue(projectId, "prices").data;
  const servers = useServers(projectId).data;
  const networks = useNetworks(projectId).data;
  const firewalls = useFirewalls(projectId).data;
  const sshKeys = useSshKeys(projectId).data;
  const publicIps = usePublicIps(projectId).data;
  const volumes = useVolumes(projectId).data;
  const floatingIps = useFloatingIps(projectId).data;
  const running = useRunningOperations(projectId).data;
  const activities = useProjectActivities(projectId).data;
  if (project.isPending) return <Spinner label={t("common.loading")} />;
  if (project.error) return <ErrorMessage error={project.error} />;

  const params = { projectId };
  const runningServers = servers?.filter((s) => s.status === "running").length ?? 0;
  const estimate = servers && types && prices ? estimateMonthly(servers, types, prices) : null;
  // Volumes are billed for their whole size, attached or not.
  const volumeCost =
    volumes && prices
      ? volumes.reduce((sum, v) => sum + volumeMonthly(v.sizeGb, prices.volumePerGbMonth.net), 0)
      : 0;
  // Floating IPs are billed whether they point at a server or not.
  const floatingCost = (floatingIps ?? []).reduce(
    (sum, ip) => sum + (prices ? (floatingIpMonthly(ip, prices) ?? 0) : 0),
    0,
  );
  const fmt = (amount: number) => money(String(amount), prices?.currency ?? "EUR", i18n.language);
  const secured = me.passkeyCount > 0 || me.totpEnabled;
  const hasServers = (servers?.length ?? 0) > 0;
  const link = (to: ProjectPath, label: string) => (
    <Link to={to} params={params} className="text-sm text-accent hover:underline">
      {label}
    </Link>
  );

  return (
    <>
      <PageHeader
        title={project.data.name}
        intro={t("overview.intro")}
        meta={<ConnectionBadges project={project.data} />}
        actions={
          // Guests may only look (docs/users.md).
          project.data.access !== "guest" && (
            <Link
              to="/projects/$projectId/servers/new"
              params={params}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-strong"
            >
              <Plus className="size-4" aria-hidden />
              {t("overview.createServer")}
            </Link>
          )
        }
      />

      {/* Size containers: the columns follow the page, which a side panel may narrow. */}
      <div className="@container">
        <div className="grid gap-3 @xl:grid-cols-2 @2xl:grid-cols-3 @4xl:grid-cols-5">
          <Counter
            icon={Server}
            label={t("nav.servers")}
            count={servers?.length}
            detail={servers ? t("overview.running", { count: runningServers }) : undefined}
            to="/projects/$projectId/servers"
            projectId={projectId}
          />
          <Counter
            icon={Network}
            label={t("nav.networks")}
            count={networks?.length}
            to="/projects/$projectId/networks"
            projectId={projectId}
          />
          <Counter
            icon={ShieldCheck}
            label={t("nav.firewalls")}
            count={firewalls?.length}
            to="/projects/$projectId/firewalls"
            projectId={projectId}
          />
          <Counter
            icon={KeyRound}
            label={t("nav.sshKeys")}
            count={sshKeys?.length}
            to="/projects/$projectId/ssh-keys"
            projectId={projectId}
          />
          <Counter
            icon={Globe}
            label={t("nav.ips")}
            count={publicIps?.length}
            to="/projects/$projectId/ips"
            projectId={projectId}
          />
        </div>
      </div>

      <div className="@container">
        <div className="grid gap-6 @2xl:grid-cols-2">
          <Section
            title={
              <span className="inline-flex items-center gap-1.5">
                {t("overview.costTitle")}
                <FieldHint text={t("overview.costHint")} topic="getting-started" />
              </span>
            }
            description={t("overview.costLead")}
          >
            <Card className="flex flex-col gap-4">
              <MonthSummary projectId={projectId} />
            </Card>
            <Card>
              {estimate ? (
                <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
                  <dt className="text-muted">{t("overview.costServers")}</dt>
                  <dd className="text-right tabular-nums">{fmt(estimate.servers)}</dd>
                  <dt className="text-muted">{t("overview.costIpv4")}</dt>
                  <dd className="text-right tabular-nums">{fmt(estimate.ipv4)}</dd>
                  <dt className="text-muted">{t("overview.costBackups")}</dt>
                  <dd className="text-right tabular-nums">{fmt(estimate.backups)}</dd>
                  {volumeCost > 0 && (
                    <>
                      <dt className="text-muted">{t("overview.costVolumes")}</dt>
                      <dd className="text-right tabular-nums">{fmt(volumeCost)}</dd>
                    </>
                  )}
                  {floatingCost > 0 && (
                    <>
                      <dt className="text-muted">{t("overview.costFloatingIps")}</dt>
                      <dd className="text-right tabular-nums">{fmt(floatingCost)}</dd>
                    </>
                  )}
                  <dt className="border-t border-line pt-2 font-medium">
                    {t("overview.costTotal")}
                  </dt>
                  <dd className="border-t border-line pt-2 text-right text-lg font-semibold tabular-nums">
                    {fmt(estimate.total + volumeCost + floatingCost)}
                  </dd>
                  {estimate.unpriced > 0 && (
                    <dd className="col-span-2 text-xs text-warn">
                      {t("overview.costUnpriced", { count: estimate.unpriced })}
                    </dd>
                  )}
                </dl>
              ) : (
                <Spinner label={t("common.loading")} />
              )}
            </Card>
          </Section>

          <Section title={t("overview.activityTitle")} description={t("overview.activityLead")}>
            <Card className="py-1">
              {running && running.length > 0 && (
                <ul className="divide-y divide-line border-b border-line">
                  {running.map((operation) => (
                    <li key={operation.id} className="flex flex-col gap-1.5 py-2.5 text-sm">
                      <span className="flex justify-between gap-2">
                        <span className="truncate">
                          {t(`overview.kinds.${operation.kind}`, {
                            defaultValue: operation.kind,
                          })}{" "}
                          <span className="text-muted">{operation.resources[0]?.name}</span>
                        </span>
                        <span className="tabular-nums text-muted">{operation.progress}%</span>
                      </span>
                      <span className="h-1 overflow-hidden rounded bg-surface-2" aria-hidden>
                        <span
                          className="block h-full bg-accent"
                          style={{ width: `${Math.max(4, operation.progress)}%` }}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {activities && activities.length === 0 && (
                <p className="py-3 text-sm text-muted">{t("overview.activityEmpty")}</p>
              )}
              <ul className="divide-y divide-line">
                {activities?.map((activity) => (
                  <li
                    key={activity.id}
                    className="flex items-start justify-between gap-3 py-2.5 text-sm"
                  >
                    <span className="min-w-0">
                      <span className={clsx(activity.status === "error" && "text-danger")}>
                        {t(`overview.kinds.${activity.kind}`, { defaultValue: activity.kind })}
                      </span>{" "}
                      <span className="font-medium">{activity.resource.name}</span>
                      {activity.actor && (
                        <span className="block text-xs text-muted">{activity.actor.name}</span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-muted">
                      {activity.status === "error" && (
                        <AskAssistantButton
                          compact
                          request={operationQuestion(
                            t,
                            activity,
                            t(`overview.kinds.${activity.kind}`, { defaultValue: activity.kind }),
                          )}
                        />
                      )}
                      {relativeTime(activity.at, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </Section>
        </div>
      </div>

      {project.data.access !== "guest" &&
        !(hasServers && secured && (sshKeys?.length ?? 0) > 0) && (
          <Section
            title={t("overview.stepsTitle")}
            description={t("overview.stepsLead")}
            actions={<HelpLink topic="getting-started" />}
          >
            <Card className="py-1">
              <ol className="divide-y divide-line">
                <Step done title={t("overview.stepConnect")}>
                  {t("overview.stepConnectText")}
                </Step>
                <Step
                  done={secured}
                  title={t("overview.stepSecure")}
                  action={
                    <Link to="/security" className="text-sm text-accent hover:underline">
                      {t("overview.openSecurity")}
                    </Link>
                  }
                >
                  {t("overview.stepSecureText")}
                </Step>
                <Step
                  done={hasServers}
                  title={t("overview.stepCatalogue")}
                  action={link("/projects/$projectId/catalogue", t("overview.openCatalogue"))}
                >
                  {t("overview.stepCatalogueText")}
                </Step>
                <Step
                  done={(sshKeys?.length ?? 0) > 0}
                  title={t("overview.stepKey")}
                  action={link("/projects/$projectId/ssh-keys", t("overview.openSshKeys"))}
                >
                  {t("overview.stepKeyText")}
                </Step>
                <Step
                  done={hasServers}
                  title={t("overview.stepServer")}
                  action={link("/projects/$projectId/servers/new", t("overview.createServer"))}
                >
                  {t("overview.stepServerText")}
                </Step>
              </ol>
            </Card>
          </Section>
        )}
    </>
  );
}
